import React, { useState, useEffect } from 'react';
// Assuming Layout component path is correct
import Layout from '../components/layout/Layout'; 
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION (BASED ON DASHBOARD.CSS REFERENCES)
const colors = {
    // Dashboard.css Background/Elements
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    // Primary Colors and Gradients from Dashboard.css
    primary: '#6366F1', // Indigo/Violet
    secondary: '#8B5CF6', // Purple/Pink
    accent: '#06B6D4', // Cyan/Blue Accent
    success: '#10B981', // Green
    warning: '#F59E0B', // Orange
    danger: '#EF4444', // Red

    // Gradients aligned with Dashboard.css
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // For form header
    gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue/Violet (Client/Pending)
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink/Red (Editor/Secondary)
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Cyan/Light Blue (Operator/Accent)
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Light Green/Teal (Manager/Success)
    gradientAdmin: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)' // Red gradient for Admin
};

// Array defining the roles with unique icons and colors
const userTypes = [
    { value: 'client', label: 'Client', icon: 'fas fa-user', color: colors.primary, gradient: colors.gradient1 },
    { value: 'editor', label: 'Editor', icon: 'fas fa-edit', color: colors.secondary, gradient: colors.gradient2 },
    { value: 'designer', label: 'Designer', icon: 'fas fa-palette', color: '#E91E63', gradient: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)' },
    { value: 'operator', label: 'Operator', icon: 'fas fa-cogs', color: colors.accent, gradient: colors.gradient3 },
    { value: 'manager', label: 'Manager', icon: 'fas fa-tasks', color: colors.success, gradient: colors.gradient4 },
    { value: 'admin', label: 'Admin', icon: 'fas fa-user-shield', color: colors.danger, gradient: colors.gradientAdmin }
];


function AddUsers() {
    // Load form data from localStorage on component mount
    const getInitialFormData = () => {
        const savedData = localStorage.getItem('addUserFormData');
        if (savedData) {
            return JSON.parse(savedData);
        }
        return {
            userType: 'client',
            name: '',
            email: '',
            password: '',
            status: 'Active',
            clientDescription: '', 
            socialMediaProfile: ''
        };
    };

    const [formData, setFormData] = useState(getInitialFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [createdUser, setCreatedUser] = useState(null); 

    // 💡 IMPORTANT: Verify this API path matches your server setup!

    // Save form data to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('addUserFormData', JSON.stringify(formData));
    }, [formData]);

    // Clear localStorage when component unmounts or when form is successfully submitted
    useEffect(() => {
        return () => {
            // Optional: Clear localStorage when leaving the page
            // localStorage.removeItem('addUserFormData');
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleRoleSelect = (userType) => {
        setFormData(prevState => ({
            ...prevState,
            userType
        }));
    };

    // 🚀 CORRECTED SUBMIT FUNCTION WITH REAL FETCH API CALL 🚀
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        setCreatedUser(null);

        // 1. Data Construction (Conditionally include client fields)
        const dataToSend = {
            userType: formData.userType,
            name: formData.name,
            email: formData.email,
            password: formData.password,
            status: formData.status,
            ...(formData.userType === 'client' && {
                // Use null if the input is empty so PHP can handle it correctly
                clientDescription: formData.clientDescription || null,
                socialMediaProfile: formData.socialMediaProfile || null
            })
        };

        try {
            // 2. Real API Call
            const response = await fetch(`${API_BASE}/add_user.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend)
            });

            // Handle non-200 HTTP statuses
            if (!response.ok) {
                throw new Error(`Server returned status code: ${response.status}`);
            }

            const result = await response.json(); 

            // 3. Handle Backend Response
            if (result.success) {
                // Success Logic
                // Capture the full data, including client details if applicable
                const newUser = {
                    name: formData.name,
                    email: formData.email,
                    role: formData.userType,
                    status: formData.status,
                    clientDescription: formData.userType === 'client' ? (formData.clientDescription || 'N/A') : undefined,
                    socialMediaProfile: formData.userType === 'client' ? (formData.socialMediaProfile || 'N/A') : undefined
                };
                
                setCreatedUser(newUser);
                setSuccess('User account created successfully!');
                
                // Clear localStorage on successful submission
                localStorage.removeItem('addUserFormData');
                
                // Reset form for next entry
                setFormData({
                    userType: 'client',
                    name: '',
                    email: '',
                    password: '',
                    status: 'Active',
                    clientDescription: '',
                    socialMediaProfile: ''
                });
                setShowPassword(false);
                
            } else {
                // Error Logic (using the specific message from PHP)
                setError(result.message || 'An unexpected server error occurred.'); 
            }
        } catch (error) {
            // Network or JSON Parsing Error
            console.error('API Call Failed:', error.message);
            setError(`Connection failed. Please ensure your PHP server is running and CORS headers are correct. Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    // 🚀 END OF CORRECTED SUBMIT FUNCTION 🚀

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const getRoleDescription = (userType) => {
        const descriptions = {
            client: 'Access to personal dashboard and basic features',
            editor: 'Content creation and editing permissions',
            designer: 'Graphic design and visual content creation access',
            operator: 'System operations and maintenance access',
            manager: 'User management and reporting capabilities',
            admin: 'Full system control, configuration, and security' 
        };
        return descriptions[userType] || 'User account with basic access';
    };

    const resetForm = () => {
        setFormData({
            userType: 'client',
            name: '',
            email: '',
            password: '',
            status: 'Active',
            clientDescription: '',
            socialMediaProfile: ''
        });
        setError('');
        setSuccess('');
        setCreatedUser(null);
        // Clear localStorage when manually resetting form
        localStorage.removeItem('addUserFormData');
    };

    // Clear form data when leaving the page (optional)
    useEffect(() => {
        const handleBeforeUnload = () => {
            // You can choose to keep the data or clear it when leaving
            // localStorage.removeItem('addUserFormData');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return (
        <Layout>
            <div className="add-users-container" style={styles.container}>
                {/* Animated Background */}
                <div className="animated-background" style={styles.animatedBackground}>
                    <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div className="grid-overlay" style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div className="add-users-header" style={styles.header}>
                    <div className="header-content" style={styles.headerContent}>
                        <div className="welcome-section">
                            <div className="greeting" style={styles.greeting}>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-user-plus me-2"></i>
                                    Create New User
                                </h1>
                                <div className="welcome-badge" style={styles.welcomeBadge}>
                                    <span className="badge-text" style={styles.badgeText}>User Management</span>
                                    <span className="badge-role" style={{...styles.badgeRole, background: colors.gradientAdmin}}>
                                        ADMIN PANEL
                                    </span>
                                </div>
                            </div>
                            <p className="page-subtitle" style={styles.pageSubtitle}>
                                Set up user accounts with appropriate access levels and required details
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="add-users-content" style={styles.content}>
                    <div className="form-section" style={styles.formSection}>
                        <div className="form-card" style={styles.formCard}>
                            <div className="form-card-header" style={styles.formCardHeader}>
                                <h3 style={styles.formCardTitle}>
                                    <i className="fas fa-user-plus me-2"></i>
                                    User Account Setup
                                </h3>
                                <p style={styles.formCardSubtitle}>Create new user accounts with specific roles and permissions</p>
                            </div>
                            <div className="form-card-body" style={styles.formCardBody}>
                                
                                {/* Success/Error Messages */}
                                {success && createdUser && (
                                    <div className="success-panel" style={styles.successPanel}>
                                        <div className="success-header" style={styles.successHeader}>
                                            <div className="success-icon" style={styles.successIcon}>
                                                <i className="fas fa-check-circle"></i>
                                            </div>
                                            <h3 style={styles.successTitle}>Account Created Successfully!</h3>
                                        </div>
                                        <div className="user-details" style={styles.userDetails}>
                                            
                                            {/* Account Credentials */}
                                            <h4 style={styles.successSectionTitle}>Account Credentials</h4>
                                            <div className="detail-item" style={styles.detailItem}>
                                                <span style={styles.detailLabel}>Name:</span>
                                                <span style={styles.detailValue}>{createdUser.name}</span>
                                            </div>
                                            <div className="detail-item" style={styles.detailItem}>
                                                <span style={styles.detailLabel}>Email:</span>
                                                <span style={styles.detailValue}>{createdUser.email}</span>
                                            </div>
                                            <div className="detail-item" style={styles.detailItem}>
                                                <span style={styles.detailLabel}>Role:</span>
                                                <span style={{
                                                    ...styles.roleBadge,
                                                    background: userTypes.find(t => t.value === createdUser.role)?.gradient
                                                }}>
                                                    {createdUser.role.toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="detail-item" style={styles.detailItem}>
                                                <span style={styles.detailLabel}>Status:</span>
                                                <span style={styles.statusBadgeActive}>{createdUser.status}</span>
                                            </div>

                                            {/* 🆕 CLIENT-SPECIFIC DETAILS (Conditionally Displayed AFTER Credentials) */}
                                            {createdUser.role === 'client' && (
                                                <>
                                                    <h4 style={styles.successSectionTitle}>Client Profile Details</h4>
                                                    <div className="detail-item" style={styles.detailItem}>
                                                        <span style={styles.detailLabel}>Description:</span>
                                                        <span style={styles.detailValue}>{createdUser.clientDescription}</span>
                                                    </div>
                                                    <div className="detail-item" style={styles.detailItem}>
                                                        <span style={styles.detailLabel}>Social Profile:</span>
                                                        <span style={styles.detailValue}>
                                                            {createdUser.socialMediaProfile !== 'N/A' ? (
                                                                <a href={createdUser.socialMediaProfile} target="_blank" rel="noopener noreferrer" style={{color: colors.accent}}>
                                                                    View Link <i className="fas fa-external-link-alt" style={{fontSize: '0.75rem'}}></i>
                                                                </a>
                                                            ) : 'N/A'}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {/* END CLIENT-SPECIFIC DETAILS */}

                                        </div>
                                        <div className="success-note" style={styles.successNote}>
                                            <i className="fas fa-info-circle me-2"></i>
                                            <span>The user can now login with the provided credentials.</span>
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div className="error-alert" style={styles.errorAlert}>
                                        <div className="alert-icon" style={styles.alertIcon}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="alert-message" style={styles.alertMessage}>
                                            <strong>Unable to Create Account</strong>
                                            <span>{error}</span>
                                        </div>
                                        <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                )}

                                {/* FORM (Hidden after success) */}
                                {!success && (
                                    <form onSubmit={handleSubmit} className="user-form" style={styles.userForm}>
                                        
                                        {/* 1. SET ROLE SECTION */}
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>Set User Role</label>
                                            <div className="role-grid" style={styles.roleGrid}>
                                                {userTypes.map((type) => (
                                                    <div 
                                                        key={type.value}
                                                        className={`role-card ${formData.userType === type.value ? 'selected' : ''}`}
                                                        onClick={() => handleRoleSelect(type.value)}
                                                        style={
                                                            formData.userType === type.value 
                                                                ? {...styles.roleCard, ...styles.roleCardSelected, borderColor: type.color}
                                                                : styles.roleCard
                                                        }
                                                    >
                                                        <div className="role-icon" style={{
                                                            ...styles.roleIcon,
                                                            background: type.gradient
                                                        }}>
                                                            <i className={type.icon}></i>
                                                        </div>
                                                        <div className="role-info" style={styles.roleInfo}>
                                                            <div className="role-label" style={styles.roleLabel}>{type.label}</div>
                                                            <div className="role-description" style={styles.roleDescription}>
                                                                {getRoleDescription(type.value)}
                                                            </div>
                                                        </div>
                                                        <div className="role-check" style={{
                                                            ...styles.roleCheck,
                                                            opacity: formData.userType === type.value ? 1 : 0
                                                        }}>
                                                            <i className="fas fa-check"></i>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* 3. Basic Account Details (Including Password) */}
                                        <div className="form-section-divider" style={styles.formSectionDivider}>
                                            <i className="fas fa-user-circle me-2"></i> Account Credentials
                                        </div>
                                        
                                        <div className="form-row" style={styles.formRow}>
                                            <div className="form-group" style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    Full Name
                                                    <span style={styles.required}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    style={styles.formInput}
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="Enter user's full name"
                                                />
                                            </div>

                                            <div className="form-group" style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    Email Address
                                                    <span style={styles.required}>*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    style={styles.formInput}
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="Enter user's email address"
                                                />
                                            </div>
                                        </div>

                                        {/* 4. Password Input */}
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>
                                                Password
                                                <span style={styles.required}>*</span>
                                            </label>
                                            <div className="password-input" style={styles.passwordInput}>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    style={styles.formInput}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="Create a secure password"
                                                    minLength="6"
                                                />
                                                <button
                                                    type="button"
                                                    className="password-toggle"
                                                    onClick={togglePasswordVisibility}
                                                    style={styles.passwordToggle}
                                                >
                                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                </button>
                                            </div>
                                            <div style={styles.formHelp}>
                                                <i className="fas fa-shield-alt me-2"></i>
                                                Minimum 6 characters with letters and numbers
                                            </div>
                                        </div>

                                        {/* 5. Status and Security Notice */}
                                        <div className="form-row" style={styles.formRow}>
                                            <div className="form-group" style={styles.formGroup}>
                                                <label style={styles.formLabel}>Account Status</label>
                                                <select
                                                    style={styles.formInput}
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </select>
                                            </div>

                                            <div className="form-group" style={styles.formGroup}>
                                                <div className="security-notice" style={styles.securityNotice}>
                                                    <div className="security-icon" style={styles.securityIcon}>
                                                        <i className="fas fa-lock"></i>
                                                    </div>
                                                    <div className="security-text" style={styles.securityText}>
                                                        <div style={styles.securityTitle}>Secure Account Creation</div>
                                                        <div style={styles.securityDescription}>
                                                            All user data is encrypted and stored securely
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Client-Specific Fields (Conditionally Rendered) - CORRECTED POSITION */}
                                        {formData.userType === 'client' && (
                                            <>
                                                <div className="form-section-divider" style={styles.formSectionDivider}>
                                                    <i className="fas fa-id-card-alt me-2"></i> Client Specific Details
                                                </div>

                                                <div className="form-row" style={styles.formRow}>
                                                    <div className="form-group" style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                                                        <label style={styles.formLabel}>
                                                            Description (Client Profile)
                                                        </label>
                                                        <textarea
                                                            style={{...styles.formInput, minHeight: '100px'}}
                                                            name="clientDescription"
                                                            value={formData.clientDescription}
                                                            onChange={handleInputChange}
                                                            placeholder="Add a brief description or note about this client (e.g., industry, main contact person)"
                                                        ></textarea>
                                                        <div style={styles.formHelp}>
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            This information helps staff manage the client relationship better.
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="form-row" style={styles.formRow}>
                                                    <div className="form-group" style={styles.formGroup}>
                                                        <label style={styles.formLabel}>
                                                            Social Media Profile Link
                                                        </label>
                                                        <input
                                                            type="url"
                                                            style={styles.formInput}
                                                            name="socialMediaProfile"
                                                            value={formData.socialMediaProfile}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., https://linkedin.com/clientname"
                                                        />
                                                        <div style={styles.formHelp}>
                                                            <i className="fas fa-link me-2"></i>
                                                            Optional: Provide a link to the client's public profile.
                                                        </div>
                                                    </div>

                                                    <div className="form-group" style={styles.formGroup}></div>
                                                </div>
                                            </>
                                        )}

                                        {/* Form Actions */}
                                        <div className="form-actions" style={styles.formActions}>
                                            <button
                                                type="button"
                                                className="reset-btn"
                                                onClick={resetForm}
                                                style={styles.resetBtn}
                                            >
                                                <i className="fas fa-undo me-2"></i>
                                                Reset Form
                                            </button>
                                            <button
                                                type="submit"
                                                className="submit-btn"
                                                disabled={isSubmitting}
                                                style={{
                                                    ...styles.submitBtn,
                                                    opacity: isSubmitting ? 0.6 : 1,
                                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner" style={styles.spinner}></span>
                                                        Creating Account...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-user-plus me-2"></i>
                                                        Create User Account
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                )}
                                
                                {/* Actions after Success */}
                                {success && (
                                    <div className="form-actions" style={styles.formActions}>
                                        <button
                                            type="button"
                                            className="another-user-btn"
                                            onClick={resetForm}
                                            style={styles.anotherUserBtn}
                                        >
                                            <i className="fas fa-plus me-2"></i>
                                            Create Another User
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes float {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
            `}</style>
        </Layout>
    );
}

// ------------------------------------
// Styles Object (Unified to Dashboard/Global Theme)
// ------------------------------------
const styles = {
    // Consistent Dark Background
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    // Floating Orbs/Background elements (Updated to use global colors)
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.primary} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: { 
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: { 
        background: colors.danger, // This is overridden inline by gradientAdmin
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textLight, // Using defined text color
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    formSection: {
        marginBottom: '40px'
    },
    formCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '25px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        boxShadow: '0 10px 20px rgba(0,0,0,0.3)'
    },
    formCardHeader: {
        background: colors.gradientMain,
        color: 'white',
        padding: '30px'
    },
    formCardTitle: {
        fontSize: '1.8rem',
        fontWeight: '700',
        margin: '0 0 8px 0'
    },
    formCardSubtitle: {
        color: 'rgba(255, 255, 255, 0.9)',
        margin: 0,
        fontSize: '1rem'
    },
    formCardBody: {
        padding: '40px'
    },
    successPanel: {
        background: `rgba(${parseInt(colors.success.slice(1, 3), 16)}, ${parseInt(colors.success.slice(3, 5), 16)}, ${parseInt(colors.success.slice(5, 7), 16)}, 0.1)`,
        border: `1px solid ${colors.success}`,
        borderRadius: '15px',
        padding: '30px'
    },
    successHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px'
    },
    successIcon: {
        color: colors.success,
        fontSize: '2rem'
    },
    successTitle: {
        color: 'white',
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: '600'
    },
    userDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    detailItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        borderBottom: `1px solid ${colors.borderDark}`,
        paddingBottom: '8px',
    },
    detailLabel: {
        color: colors.textLight,
        fontWeight: '600',
        minWidth: '120px'
    },
    detailValue: {
        color: 'white',
        fontWeight: '500',
        wordBreak: 'break-all',
        flex: 1
    },
    // Updated Section Title Style for Success Panel
    successSectionTitle: {
        color: colors.accent, // Use accent color for distinction
        fontWeight: '700',
        fontSize: '1.1rem',
        marginTop: '20px',
        marginBottom: '10px',
        borderBottom: `1px solid ${colors.accent}50`,
        paddingBottom: '5px'
    },
    roleBadge: {
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    statusBadgeActive: {
        background: colors.success,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    successNote: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: colors.textLight,
        marginTop: '20px',
        fontSize: '0.9rem'
    },
    errorAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        color: colors.danger,
        padding: '20px',
        borderRadius: '15px',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.danger}80`
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    userForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    formLabel: {
        color: 'white',
        fontWeight: '600',
        fontSize: '1rem'
    },
    required: {
        color: colors.danger,
        marginLeft: '4px'
    },
    roleGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '15px'
    },
    roleCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '15px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column', 
        alignItems: 'flex-start',
        gap: '10px',
        position: 'relative'
    },
    roleCardSelected: {
        background: 'rgba(255, 255, 255, 0.1)',
        transform: 'translateY(-2px)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        borderStyle: 'solid',
    },
    roleIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        color: 'white',
        marginBottom: '5px'
    },
    roleInfo: {
        flex: 1
    },
    roleLabel: {
        color: 'white',
        fontWeight: '700',
        fontSize: '1.1rem',
        marginBottom: '4px'
    },
    roleDescription: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.8rem',
        lineHeight: '1.4'
    },
    roleCheck: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        color: colors.success,
        fontSize: '1.5rem',
        transition: 'opacity 0.3s ease'
    },
    formSectionDivider: {
        color: 'white',
        fontSize: '1.3rem',
        fontWeight: '600',
        borderBottom: `1px solid ${colors.borderDark}`,
        paddingBottom: '10px',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.9
    },
    formRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '25px'
    },
    formInput: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        boxSizing: 'border-box',
        resize: 'none', 
    },
    passwordInput: {
        position: 'relative'
    },
    passwordToggle: {
        position: 'absolute',
        right: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.7)',
        cursor: 'pointer',
        fontSize: '1rem',
        padding: '8px',
        borderRadius: '6px',
        transition: 'background 0.3s ease'
    },
    formHelp: {
        color: colors.textLight,
        fontSize: '0.85rem',
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    securityNotice: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: `1px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        height: '100%' 
    },
    securityIcon: {
        color: colors.success,
        fontSize: '1.5rem'
    },
    securityText: {
        flex: 1
    },
    securityTitle: {
        color: 'white',
        fontWeight: '600',
        marginBottom: '4px'
    },
    securityDescription: {
        color: colors.textLight,
        fontSize: '0.9rem'
    },
    formActions: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'flex-end',
        marginTop: '30px'
    },
    resetBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 24px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center'
    },
    submitBtn: {
        background: colors.gradientMain,
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center'
    },
    anotherUserBtn: {
        background: colors.gradientMain,
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center'
    },
    spinner: {
        width: '16px',
        height: '16px',
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginRight: '8px'
    }
};

export default AddUsers;